import { api } from "@/services/api"

// ═══════════════════════════════════════════════════════════════════
//  HOST RESCHEDULE
//
//  A host moving their own appointment to a free public or private time
//  (never off-slot, AB#779), on the appointment's own day. Mirrors AmsBookingRescheduleController; every rule
//  is the server's (HostRescheduleRules.cs) — the WebUI only shows what the
//  options endpoint says and surfaces the server's refusal text.
// ═══════════════════════════════════════════════════════════════════

/** Matches HostRescheduleStart: a time the booking could move to, and the event type it would be booked on. */
export interface HostRescheduleStart {
  /** ISO instant. */
  start: string
  /** `intake` (a public slot) or `private`; never `off-slot`. */
  eventTypeSlug: string
}

/** Matches HostRescheduleOptions. */
export interface HostRescheduleOptions {
  /** Whether the signed-in person may move this booking now. */
  allowed: boolean
  /** Why not, in words fit to show. Null when allowed. */
  deniedReason?: string | null
  /** The host's free public and private starts, ascending. */
  starts: HostRescheduleStart[]
  /**
   * Starts the host is rostered for but already booked across — where an administrator's
   * double-booking override could move it. Empty (or absent) for anybody else.
   */
  busyStarts?: HostRescheduleStart[]
}

/** HostRescheduleOutcome, as the API serializes it (a number). */
export const HostRescheduleOutcome = {
  Rescheduled: 0,
  NotFound: 1,
  Forbidden: 2,
  NotAllowed: 3,
  SlotUnavailable: 4,
  CalFailed: 5,
  /** Somebody else took the new time in the moment it was being booked; the move was undone (409 `slotUnavailable`). */
  SlotTaken: 6,
  /** The host is booked at the new time (409 `hostAlreadyBooked`); an administrator may double-book. */
  HostAlreadyBooked: 7,
} as const

/** Matches HostRescheduleResult. */
export interface HostRescheduleResult {
  outcome: number
  newBookingRequestId?: string | null
  newCalBookingUid?: string | null
  /** A warning on success (e.g. Cal put it on another host), or the failure's explanation. */
  message?: string | null
}

/** Matches HostRescheduleRequest. The reason is internal: it is never sent to the household. */
export interface HostRescheduleRequest {
  requestId: string
  /** One of `HostRescheduleOptions.starts` (or `busyStarts`), its `start` exactly as it came. */
  newStart: string
  /** That start's `eventTypeSlug`: the replacement is booked on it. */
  eventTypeSlug: string
  reason: string
  /** An administrator double-booking the host at `newStart`. Sent only with `overrideReason`. */
  overrideDoubleBooking?: boolean
  overrideReason?: string
}

// ═══════════════════════════════════════════════════════════════════
//  ADMIN RESCHEDULE (AB#610)
//
//  A manager moving an appointment to any free time of its own slot type at
//  its own site in the next 28 Pacific days. Mirrors the admin-reschedule
//  actions on AmsBookingRescheduleController; the server picks the host
//  (the current one if free, else the first free one) and re-checks every
//  rule (AdminRescheduleRules.cs) — the WebUI shows what it offers and
//  surfaces its refusal text.
// ═══════════════════════════════════════════════════════════════════

/** Matches AdminRescheduleHost: somebody free at a start. */
export interface AdminRescheduleHost {
  calUserId: number
  name: string
  /** Hosts the appointment now, and keeps it if it moves here. */
  isCurrent: boolean
}

/** Matches AdminRescheduleStart. */
export interface AdminRescheduleStart {
  /** ISO instant, sent back exactly as it came. */
  start: string
  /** Who is free then; the first is who the server would give it to. */
  hosts: AdminRescheduleHost[]
}

/** Matches AdminRescheduleDay: one Pacific day of free starts. */
export interface AdminRescheduleDay {
  /** "YYYY-MM-DD", the Pacific calendar day. */
  date: string
  starts: AdminRescheduleStart[]
}

/** Matches AdminRescheduleOptions. */
export interface AdminRescheduleOptions {
  allowed: boolean
  deniedReason?: string | null
  /** The appointment's slot type, which every start offered is of. */
  eventTypeSlug?: string | null
  days: AdminRescheduleDay[]
}

/** Matches AdminRescheduleResult. */
export interface AdminRescheduleResult {
  outcome: number
  newBookingRequestId?: string | null
  newCalBookingUid?: string | null
  hostCalUserId?: number | null
  /** A warning on success (e.g. Cal put it on another host), or the failure's explanation. */
  message?: string | null
}

/** Matches AdminRescheduleRequest, plus the booking it is about. The reason is internal. */
export interface AdminRescheduleRequest {
  uid: string
  /** The CMS booking request, when known, so its notes list refreshes with the move's internal note. */
  requestId?: string
  /** One of the options' starts, exactly as it came. */
  newStart: string
  reason: string
}

// ═══════════════════════════════════════════════════════════════════
//  CHANGE OF APPOINTMENT TYPE (AB#767)
//
//  A manager changing a booked appointment's type, keeping its host and
//  time. Mirrors ChangeType on AmsBookingRescheduleController; every check
//  is the server's (AppointmentTypeChangeRules.cs). Recorded as a reschedule
//  (new reference code) that tells the household nothing.
// ═══════════════════════════════════════════════════════════════════

/** AppointmentTypeChangeOutcome, as the API serializes it (a number). */
export const AppointmentTypeChangeOutcome = {
  Changed: 0,
  NotFound: 1,
  Refused: 2,
  CalFailed: 3,
  SlotTaken: 4,
} as const

/** Matches AppointmentTypeChangeResult. */
export interface AppointmentTypeChangeResult {
  outcome: number
  newBookingRequestId?: string | null
  newCalBookingUid?: string | null
  /** The new booking's type. */
  eventTypeSlug?: string | null
  /** The same host. */
  hostCalUserId?: number | null
  /** The same start. */
  slotStart?: string | null
  /** A warning on success (e.g. Cal put it on another host), or the failure's explanation. */
  message?: string | null
}

/** Matches AppointmentTypeChangeRequest, plus the booking it is about. The reason is internal. */
export interface AppointmentTypeChangeRequest {
  uid: string
  /** The CMS booking request, when known, so its notes list refreshes with the change's internal note. */
  requestId?: string
  /** `intake`, `private` or `off-slot`, other than the booking's own. */
  newEventTypeSlug: string
  reason: string
}

// The same tags the calendar uses, so a move refetches the grid and the status view — and notesApi's,
// because the server writes the move's reason as a Reschedule note on the booking moved.
const taggedApi = api.enhanceEndpoints({ addTagTypes: ["AmsBookings", "AmsBookingStatus", "AmsBookingNotes"] })

export const rescheduleApi = taggedApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Not cached across opens: the answer depends on the wall clock (same day, not yet started) and on
     * what is free right now.
     */
    getHostRescheduleOptions: builder.query<HostRescheduleOptions, string>({
      query: (requestId) => `/api/ams/booking-requests/${encodeURIComponent(requestId)}/host-reschedule/options`,
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, requestId) => [{ type: "AmsBookingStatus" as const, id: requestId }],
    }),
    rescheduleAsHost: builder.mutation<HostRescheduleResult, HostRescheduleRequest>({
      query: ({ requestId, newStart, eventTypeSlug, reason, overrideDoubleBooking, overrideReason }) => ({
        url: `/api/ams/booking-requests/${encodeURIComponent(requestId)}/host-reschedule`,
        method: "POST",
        body: { newStart, eventTypeSlug, reason, overrideDoubleBooking, overrideReason },
      }),
      invalidatesTags: (_result, _error, { requestId }) => [
        "AmsBookings",
        "AmsBookingStatus",
        { type: "AmsBookingNotes" as const, id: requestId },
      ],
    }),
    /** The 28-day read: asked only when the form opens, and never cached across opens. */
    getAdminRescheduleOptions: builder.query<AdminRescheduleOptions, string>({
      query: (uid) => `/api/ams/bookings/${encodeURIComponent(uid)}/admin-reschedule-options`,
      keepUnusedDataFor: 0,
    }),
    rescheduleAsAdmin: builder.mutation<AdminRescheduleResult, AdminRescheduleRequest>({
      query: ({ uid, newStart, reason }) => ({
        url: `/api/ams/bookings/${encodeURIComponent(uid)}/admin-reschedule`,
        method: "POST",
        body: { newStart, reason },
      }),
      invalidatesTags: (_result, _error, { requestId }) => [
        "AmsBookings",
        "AmsBookingStatus",
        ...(requestId ? [{ type: "AmsBookingNotes" as const, id: requestId }] : []),
      ],
    }),
    changeAppointmentType: builder.mutation<AppointmentTypeChangeResult, AppointmentTypeChangeRequest>({
      query: ({ uid, newEventTypeSlug, reason }) => ({
        url: `/api/ams/bookings/${encodeURIComponent(uid)}/change-type`,
        method: "POST",
        body: { newEventTypeSlug, reason },
      }),
      invalidatesTags: (_result, _error, { requestId }) => [
        "AmsBookings",
        "AmsBookingStatus",
        ...(requestId ? [{ type: "AmsBookingNotes" as const, id: requestId }] : []),
      ],
    }),
  }),
})

export const {
  useGetHostRescheduleOptionsQuery,
  useRescheduleAsHostMutation,
  useGetAdminRescheduleOptionsQuery,
  useRescheduleAsAdminMutation,
  useChangeAppointmentTypeMutation,
} = rescheduleApi
